import { useCallback, useEffect, useRef, useState } from 'react';

import { IXOPAY_PAYMENT_JS_URL } from 'constants/payment';

import useScript from './useScript';

import type {
  IxoPayCardData,
  IxoPayFieldError,
  IxoPayFieldState,
  IxoPayInstance,
  IxoPayJsStyles,
  IxoPayTokenizeData,
} from 'types/ixopay';

export interface IxoPayJsValidity {
  validCvv: boolean;
  validNumber: boolean;
}

export interface IxoPayTokenizeResult {
  cardData: IxoPayCardData;
  token: string;
}

export interface UseIxoPayJsReturn {
  isReady: boolean;
  tokenize: (data: IxoPayTokenizeData) => Promise<IxoPayTokenizeResult>;
  validity: IxoPayJsValidity;
}

const SCRIPT_DATA_ATTRS = { main: 'payment-js' };

const IFRAME_PLACEHOLDERS = { cvv: '123', number: '1234 5678 9012 3456' };

const DEFAULT_VALIDITY: IxoPayJsValidity = {
  validCvv: false,
  validNumber: false,
};

const useIxoPayJs = (
  publicIntegrationKey: string | null | undefined,
  numberDivId: string,
  cvvDivId: string,
  iframeStyles?: IxoPayJsStyles,
): UseIxoPayJsReturn => {
  const isScriptLoaded = useScript(IXOPAY_PAYMENT_JS_URL, {
    dataAttributes: SCRIPT_DATA_ATTRS,
  });
  const instanceRef = useRef<IxoPayInstance | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [validity, setValidity] = useState<IxoPayJsValidity>(DEFAULT_VALIDITY);

  // Store the latest iframeStyles in a ref so the init callback can read the
  // current value without iframeStyles needing to be a useEffect dependency
  // (adding it would re-run the effect — and re-init payment.js — on every render
  // because the caller may pass a new object literal each time)
  const iframeStylesRef = useRef(iframeStyles);
  iframeStylesRef.current = iframeStyles;

  useEffect(() => {
    if (!isScriptLoaded || !publicIntegrationKey || !window.PaymentJs) return;

    let mounted = true;

    const paymentJs = new window.PaymentJs();
    instanceRef.current = paymentJs;
    setIsReady(false);
    setValidity(DEFAULT_VALIDITY);

    paymentJs.init(publicIntegrationKey, numberDivId, cvvDivId, () => {
      if (!mounted) return;

      const styles = iframeStylesRef.current;
      if (styles?.number) paymentJs.setNumberStyle(styles.number);
      if (styles?.cvv) paymentJs.setCvvStyle(styles.cvv);

      paymentJs.setNumberPlaceholder(IFRAME_PLACEHOLDERS.number);
      paymentJs.setCvvPlaceholder(IFRAME_PLACEHOLDERS.cvv);

      setIsReady(true);

      const onFieldUpdate = (state: IxoPayFieldState) => {
        if (!mounted) return;
        setValidity((prev) => ({
          validCvv: state.validCvv ?? prev.validCvv,
          validNumber: state.validNumber ?? prev.validNumber,
        }));
      };

      paymentJs.numberOn('input', onFieldUpdate);
      paymentJs.cvvOn('input', onFieldUpdate);
    });

    return () => {
      mounted = false;
      instanceRef.current = null;
    };
  }, [isScriptLoaded, publicIntegrationKey, numberDivId, cvvDivId]);

  const tokenize = useCallback(
    (data: IxoPayTokenizeData): Promise<IxoPayTokenizeResult> =>
      new Promise((resolve, reject) => {
        const instance = instanceRef.current;
        if (!instance) {
          reject(new Error('IxoPay payment.js is not ready'));
          return;
        }
        instance.tokenize(
          data,
          (token, cardData) => resolve({ cardData: cardData ?? {}, token }),
          (errors: IxoPayFieldError[]) => reject(errors),
        );
      }),
    [],
  );

  return { isReady, tokenize, validity };
};

export default useIxoPayJs;
